import clsx from "clsx";
import React, { LiHTMLAttributes, useEffect } from "react";
import { Conditional } from "web/react/components/conditional";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { FilterClickHandler } from "web/types/feed-filters";
import * as styles from "./category-item.css";

type ListChildItem = LiHTMLAttributes<HTMLLIElement> &
    ({ as: "a"; href: string } | { as?: "span"; href?: never });

export type CategoryItemProps = ListChildItem & {
    label: string;
    extraLabel?: string;
    onClick: FilterClickHandler;
    activeHighlighted?: boolean;
    activeShowChevron?: boolean;
    count?: number;
    showChevron?: boolean;
    id?: string;
    className?: string;
    scrollOnLoad?: (id: string) => void;
    url?: string;
};

/**
 * Design System
 * @name ListItem (Container)
 * @version 1.1
 * @design https://www.figma.com/file/VhOHx4tv9uYXZHXAAKPVYf/Design-System?node-id=13049%3A108608
 */
export const CategoryItem = ({
    as: Component = "span",
    label,
    extraLabel,
    activeHighlighted = false,
    activeShowChevron = false,
    count = -1,
    showChevron = true,
    onClick,
    id = "",
    scrollOnLoad = () => {},
    url,
    ...props
}: CategoryItemProps): JSX.Element => {
    // scroll selected category into view on render
    useEffect(() => {
        activeHighlighted &&
            window.setTimeout(() => {
                scrollOnLoad && scrollOnLoad(id);
            }, 50);
    }, [activeHighlighted, id, scrollOnLoad]);

    const { isMobileViewport, isTabletViewport } = useDomViewport();
    const isMobileView = isMobileViewport || isTabletViewport;

    return (
        <li {...props} onClick={onClick} id={id}>
            <Component
                className={clsx({
                    [styles.categoryItem]: !activeHighlighted && !isMobileView,
                    [styles.highlighted]: activeHighlighted && !isMobileView,
                    [styles.categoryItemMobile]: !activeHighlighted && isMobileView,
                    [styles.highlightedMobile]: activeHighlighted && isMobileView,
                })}
            >
                <View className={styles.labelWrapper} width={"fit-content"}>
                    <a href={url} onClick={(event) => event.preventDefault()}>
                        <p className={styles.label}>
                            {label} &nbsp;
                            {count >= 0 && <span className={styles.count}>{`(${count})`}</span>}
                            {extraLabel && <span className={styles.extraLabel}>{extraLabel}</span>}
                        </p>
                    </a>
                </View>
                <Conditional check={Boolean(showChevron)}>
                    <Text as={"span"} textStyle={"body-1"} className={styles.iconWrapper}>
                        <SVGIcon
                            name={
                                activeShowChevron
                                    ? "chevron-medium"
                                    : activeHighlighted
                                    ? "tick"
                                    : "chevron-medium"
                            }
                        />
                    </Text>
                </Conditional>
            </Component>
        </li>
    );
};
